import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, FileSpreadsheet, ChevronDown, ChevronRight, Loader2, ShoppingBag, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const todayWIB = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
const addDays = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return d.toLocaleDateString("en-CA"); };
const monthStart = (s) => s.slice(0, 8) + "01";
const MP_STYLE = { shopee: "bg-orange-100 text-orange-700", tiktok: "bg-neutral-900 text-white" };
const fmtPct = (v) => (v || v === 0 ? `${Number(v).toFixed(1)}%` : "-");

const Stat = ({ label, value, tone = "text-cocoa" }) => (
  <div className="rounded-lg border border-taupe/20 bg-white px-3 py-2 min-w-[130px]">
    <div className="text-[11px] uppercase tracking-wide text-wmuted">{label}</div>
    <div className={`text-base font-bold ${tone}`}>{value}</div>
  </div>
);

export default function RecapView({ apiBase, title, subtitle, exportPrefix = "rangkuman" }) {
  const [from, setFrom] = useState(todayWIB());
  const [to, setTo] = useState(todayWIB());
  const [est, setEst] = useState({ shopee_pct: 75, tiktok_pct: 77 });
  const [doc, setDoc] = useState({ status: "none" });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [lossOnly, setLossOnly] = useState(false);
  const poll = useRef(null);
  const reqSeq = useRef(0);

  const load = useCallback(async (f, t) => {
    const seq = ++reqSeq.current;
    try {
      const r = await api.get(`${apiBase}/daily`, { params: { date_from: f, date_to: t } });
      if (seq !== reqSeq.current) return r.data.status;  // abaikan respons usang (from/to sudah berubah)
      setDoc(r.data);
      return r.data.status;
    } catch (e) { return "error"; }
  }, [apiBase]);

  useEffect(() => {
    load(from, to);
    return () => clearInterval(poll.current);
  }, [from, to, load]);

  useEffect(() => {
    api.get(`${apiBase}/settings`).then((r) => setEst(r.data)).catch(() => {});
  }, [apiBase]);

  useEffect(() => {
    clearInterval(poll.current);
    if (doc.status === "running") {
      poll.current = setInterval(async () => {
        const st = await load(from, to);
        if (st !== "running") clearInterval(poll.current);
      }, 2500);
    }
    return () => clearInterval(poll.current);
  }, [doc.status, from, to, load]);

  const generate = async () => {
    setLoading(true);
    try {
      await api.post(`${apiBase}/generate`, { date_from: from, date_to: to });
      toast.success("Mulai menarik data...");
      await load(from, to);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doExport = () => {
    api.get(`${apiBase}/export`, { params: { date_from: from, date_to: to }, responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `${exportPrefix}-${from}_sd_${to}.xlsx`; a.click(); URL.revokeObjectURL(u); })
      .catch(() => toast.error("Export gagal / data belum siap"));
  };

  const saveEst = async () => {
    try {
      const r = await api.put(`${apiBase}/settings`, { shopee_pct: Number(est.shopee_pct), tiktok_pct: Number(est.tiktok_pct) });
      setEst(r.data);
      toast.success("Persen estimasi disimpan. Klik Tarik Ulang untuk menerapkan.");
    } catch (e) { toast.error("Gagal menyimpan persen estimasi"); }
  };

  const preset = (type) => {
    const t = todayWIB();
    if (type === "today") { setFrom(t); setTo(t); }
    else if (type === "7d") { setFrom(addDays(t, -6)); setTo(t); }
    else if (type === "month") { setFrom(monthStart(t)); setTo(t); }
  };

  const mk = doc.marketplace || {};
  const t = mk.totals || {};
  const groups = doc.groups || { rows: [], totals: {} };
  const running = doc.status === "running";
  const pr = doc.progress || { done: 0, total: 0 };
  const allOrders = mk.orders || [];
  const displayedOrders = lossOnly ? allOrders.filter((o) => (o.profit || 0) < 0) : allOrders;
  const profitTone = (v) => (v > 0 ? "text-status-passed" : v < 0 ? "text-red-500" : "text-wmuted");

  return (
    <div className="space-y-5">
      <PageHeader title={title} subtitle={subtitle} />

      <Panel>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 bg-white" data-testid="recap-from" />
          <span className="text-wmuted">s/d</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 bg-white" data-testid="recap-to" />
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="border-taupe text-cocoa" onClick={() => preset("today")} data-testid="recap-preset-today">Hari Ini</Button>
            <Button size="sm" variant="outline" className="border-taupe text-cocoa" onClick={() => preset("7d")} data-testid="recap-preset-7d">7 Hari</Button>
            <Button size="sm" variant="outline" className="border-taupe text-cocoa" onClick={() => preset("month")} data-testid="recap-preset-month">Bulan Ini</Button>
          </div>
          <div className="flex items-center gap-1 text-xs text-wmuted border-l border-taupe/30 pl-2 ml-1" title="Dana cair perkiraan = persen ini × (Harga Jual − Diskon Penjual). Voucher Marketplace tidak dikurangi (disubsidi platform). Berlaku untuk order yang belum ada di data rekonsiliasi.">
            <span>Est. belum cair:</span>
            <span className="font-medium">SHP</span>
            <Input type="number" value={est.shopee_pct} onChange={(e) => setEst((v) => ({ ...v, shopee_pct: e.target.value }))} className="w-14 h-8 bg-white" data-testid="est-shopee" />%
            <span className="font-medium ml-1">TT</span>
            <Input type="number" value={est.tiktok_pct} onChange={(e) => setEst((v) => ({ ...v, tiktok_pct: e.target.value }))} className="w-14 h-8 bg-white" data-testid="est-tiktok" />%
            <Button size="sm" variant="outline" className="border-taupe text-cocoa h-8" onClick={saveEst} data-testid="est-save">Simpan</Button>
          </div>
          <Button onClick={generate} disabled={loading || running} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="recap-generate-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            {running ? "Menarik data..." : doc.status === "done" ? "Tarik Ulang" : "Tarik Data"}
          </Button>
          {doc.status === "done" && (
            <Button variant="outline" onClick={doExport} className="border-status-passed text-status-passed" data-testid="recap-export-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />Export Excel
            </Button>
          )}
          {doc.status === "done" && doc.generated_at && (
            <span className="ml-auto text-xs text-wmuted">Diperbarui: {new Date(doc.generated_at).toLocaleString("id-ID")}</span>
          )}
        </div>
        {running && (
          <div className="mt-3 text-sm text-wmuted" data-testid="recap-progress">
            Memproses order {pr.done}/{pr.total || "..."}. Rentang panjang bisa memakan waktu — halaman terisi otomatis.
          </div>
        )}
        {doc.status === "none" && !running && (
          <div className="mt-3 text-sm text-wmuted">Belum ada data untuk rentang ini. Klik <b>Tarik Data</b> untuk mengambil data.</div>
        )}
        {doc.status === "error" && <div className="mt-3 text-sm text-red-500">Gagal: {doc.error}</div>}
        {mk.truncated && <div className="mt-2 text-xs text-amber-600">Catatan: data pada rentang ini melebihi batas tarik ({3000}), ditampilkan sebagian teratas.</div>}
      </Panel>

      {doc.status === "done" && doc.combined && (
        <div className="rounded-xl border border-cocoa/20 bg-cocoa/5 p-4" data-testid="recap-combined">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm font-semibold text-cocoa">Total Margin Harian (Gabungan: Marketplace + Grup Lain)</div>
            <div className="flex gap-3 flex-wrap" data-testid="recap-combined-totals">
              <Stat label="Total Order" value={doc.combined.orders || 0} />
              <Stat label="Nett Penjualan" value={fmtIDR(doc.combined.nett)} tone="text-cocoa" />
              <Stat label="Total HPP" value={fmtIDR(doc.combined.hpp)} tone="text-wmuted" />
              <Stat label="Profit Bersih" value={fmtIDR(doc.combined.profit)} tone={doc.combined.profit > 0 ? "text-status-passed" : doc.combined.profit < 0 ? "text-red-500" : "text-wmuted"} />
              <Stat label="Margin %" value={fmtPct(doc.combined.margin)} tone={doc.combined.profit >= 0 ? "text-status-passed" : "text-red-500"} />
            </div>
          </div>
        </div>
      )}

      {doc.status === "done" && (
        <Tabs defaultValue="marketplace">
          <TabsList>
            <TabsTrigger value="marketplace" data-testid="recap-tab-mp"><ShoppingBag className="w-4 h-4 mr-1.5" />Marketplace</TabsTrigger>
            <TabsTrigger value="products" data-testid="recap-tab-prod">Per Produk</TabsTrigger>
            <TabsTrigger value="groups" data-testid="recap-tab-groups"><Users className="w-4 h-4 mr-1.5" />Grup Lain</TabsTrigger>
          </TabsList>

          <TabsContent value="marketplace" className="mt-4 space-y-4">
            <div className="flex gap-3 flex-wrap" data-testid="recap-mp-totals">
              <Stat label="Total Order" value={t.orders || 0} />
              <Stat label="Harga Jual" value={fmtIDR(t.jual)} tone="text-cocoa" />
              <Stat label="Voucher Marketplace" value={fmtIDR(t.voucher_mp)} tone="text-blue-600" />
              <Stat label="Diskon Penjual" value={fmtIDR(t.seller_discount)} tone="text-amber-600" />
              <Stat label="Nett Sebelum Fee" value={fmtIDR(t.nett_before_fee)} tone="text-cocoa" />
              <Stat label="Fee Marketplace" value={fmtIDR(t.fee)} tone="text-red-500" />
              <Stat label="Sudah Cair (settle)" value={fmtIDR(t.nett_cair)} tone="text-status-passed" />
              <Stat label="Perkiraan Belum Cair" value={fmtIDR(t.nett_perkiraan)} tone="text-amber-600" />
              <Stat label="Total HPP" value={fmtIDR(t.hpp)} tone="text-wmuted" />
              <Stat label="Nett Profit (perkiraan)" value={fmtIDR(t.profit)} tone={profitTone(t.profit)} />
              <Stat label="Margin %" value={fmtPct(t.margin)} tone={profitTone(t.profit)} />
              <Stat label="Order Rugi" value={t.loss_orders || 0} tone={t.loss_orders ? "text-red-500" : "text-wmuted"} />
              <Stat label="Total Retur" value={fmtIDR(t.return_value)} tone={t.return_value ? "text-red-500" : "text-wmuted"} />
            </div>
            <div className="flex gap-3 text-xs text-wmuted flex-wrap">
              {t.unsettled > 0 && <span className="text-amber-600">{t.unsettled} order belum cair (belum ada di data rekonsiliasi) → nilai memakai perkiraan persen × nett penjualan.</span>}
              {t.detail_missing > 0 && <span className="text-amber-600">{t.detail_missing} order detail belum ketemu → nett memakai nilai kasir ERP sementara.</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setLossOnly((v) => !v)} data-testid="recap-loss-filter"
                className={lossOnly ? "bg-red-500 hover:bg-red-600 text-white border-red-500" : "border-red-300 text-red-500 hover:bg-red-50"}>
                {lossOnly ? "Tampilkan Semua Order" : `Hanya Order Rugi (${t.loss_orders || 0})`}
              </Button>
            </div>
            <Panel>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-8"></TableHead><TableHead>MP</TableHead><TableHead>Order SN</TableHead>
                      <TableHead>Tgl Kasir</TableHead>
                      <TableHead className="text-right">Jual</TableHead>
                      <TableHead className="text-right">Voucher MP</TableHead><TableHead className="text-right">Diskon Penjual</TableHead>
                      <TableHead className="text-right">Nett Sblm Fee</TableHead><TableHead className="text-right">Fee</TableHead>
                      <TableHead className="text-right">Nett Stlh Fee</TableHead>
                      <TableHead className="text-right">Retur</TableHead>
                      <TableHead className="text-right">HPP</TableHead><TableHead className="text-right">Profit (±)</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedOrders.length === 0 ? (
                      <TableRow><TableCell colSpan={14} className="text-center py-10 text-wmuted">{lossOnly ? "Tidak ada order rugi di rentang ini." : "Tidak ada order marketplace di rentang ini."}</TableCell></TableRow>
                    ) : displayedOrders.map((o, oi) => (
                      <React.Fragment key={o.order_sn || oi}>
                        <TableRow data-testid={`recap-order-${oi}`} className={`cursor-pointer ${o.excluded ? "opacity-50 bg-slate-50" : (o.profit || 0) < 0 ? "bg-red-50" : ""}`} onClick={() => setExpanded((e) => ({ ...e, [`mp-${o.order_sn || oi}`]: !e[`mp-${o.order_sn || oi}`] }))}>
                          <TableCell>{(o.products || []).length ? (expanded[`mp-${o.order_sn || oi}`] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}</TableCell>
                          <TableCell><span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${MP_STYLE[o.platform] || "bg-taupe/20"}`}>{o.platform || "-"}</span></TableCell>
                          <TableCell className="text-sm font-mono whitespace-nowrap">{o.order_sn}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {o.kasir_date}
                            {o.excluded ? <Badge className="bg-slate-200 text-slate-600 text-[9px] ml-1">kilat · belum dikirim</Badge> : (!o.detail_found && <Badge className="bg-amber-100 text-amber-700 text-[9px] ml-1">tanpa detail</Badge>)}
                          </TableCell>
                          <TableCell className="text-right text-sm">{fmtIDR(o.jual)}</TableCell>
                          <TableCell className="text-right text-sm text-blue-600">{o.voucher_mp ? fmtIDR(o.voucher_mp) : "-"}</TableCell>
                          <TableCell className="text-right text-sm text-amber-600">{o.seller_discount ? fmtIDR(o.seller_discount) : "-"}</TableCell>
                          <TableCell className="text-right text-sm">{fmtIDR(o.nett_before_fee)}</TableCell>
                          <TableCell className="text-right text-sm text-red-500">{o.fee ? fmtIDR(o.fee) : "-"}</TableCell>
                          <TableCell className="text-right text-sm">
                            {fmtIDR(o.nett_after_fee)}
                            <div className="text-[9px] leading-tight">{o.excluded ? null : o.settled ? <span className="text-status-passed">cair</span> : <span className="text-amber-600">perkiraan</span>}</div>
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {(o.return_value || 0) > 0 ? (
                              <span className="text-red-500">-{fmtIDR(o.return_value)}
                                <div className="text-[9px] leading-tight text-amber-600">×{o.return_qty}{o.settled ? " (sdh dipotong MP)" : ""}</div>
                              </span>
                            ) : <span className="text-wmuted">-</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm text-wmuted">{o.hpp ? fmtIDR(o.hpp) : <span className="text-amber-600 text-xs">—</span>}</TableCell>
                          <TableCell className={`text-right text-sm font-semibold ${profitTone(o.profit)}`}>{fmtIDR(o.profit)}</TableCell>
                          <TableCell className={`text-right text-sm font-medium ${profitTone(o.profit)}`}>{fmtPct(o.margin)}</TableCell>
                        </TableRow>
                        {expanded[`mp-${o.order_sn || oi}`] && (o.products || []).map((p, i) => (
                          <TableRow key={`mp-${o.order_sn || oi}-${i}`} className="bg-ivory/40">
                            <TableCell></TableCell><TableCell colSpan={2} className="text-xs text-wmuted pl-6">{p.sku || p.name}</TableCell>
                            <TableCell className="text-xs">×{p.qty}</TableCell>
                            <TableCell className="text-right text-xs" colSpan={10}>Normal {fmtIDR(p.normal)} · Jual {fmtIDR(p.jual)}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Panel>
          </TabsContent>

          <TabsContent value="products" className="mt-4">
            <Panel>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>MP</TableHead><TableHead>SKU / Produk</TableHead><TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Harga Normal</TableHead><TableHead className="text-right">Harga Jual</TableHead><TableHead className="text-right">Diskon</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(mk.products || []).length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-wmuted">Tidak ada produk.</TableCell></TableRow>
                  ) : (mk.products || []).map((p, i) => (
                    <TableRow key={i} data-testid={`recap-prod-${i}`}>
                      <TableCell><span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${MP_STYLE[p.platform] || "bg-taupe/20"}`}>{p.platform || "-"}</span></TableCell>
                      <TableCell className="text-sm"><div className="font-medium text-cocoa">{p.sku || "-"}</div><div className="text-xs text-wmuted max-w-[320px] truncate">{p.name}</div></TableCell>
                      <TableCell className="text-right text-sm">{p.qty}</TableCell>
                      <TableCell className="text-right text-sm text-wmuted">{fmtIDR(p.normal)}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{fmtIDR(p.jual)}</TableCell>
                      <TableCell className="text-right text-sm text-amber-600">{p.diskon ? fmtIDR(p.diskon) : "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          </TabsContent>

          <TabsContent value="groups" className="mt-4 space-y-4">
            <div className="flex gap-3 flex-wrap">
              <Stat label="Total Order" value={groups.totals?.orders || 0} />
              <Stat label="Total Item" value={groups.totals?.total_items || 0} />
              <Stat label="Sub Total" value={fmtIDR(groups.totals?.sub_total)} tone="text-wmuted" />
              <Stat label="Diskon" value={fmtIDR(groups.totals?.discount)} tone="text-amber-600" />
              <Stat label="Nett" value={fmtIDR(groups.totals?.nett)} tone="text-status-passed" />
              <Stat label="Total HPP" value={fmtIDR(groups.totals?.hpp)} tone="text-wmuted" />
              <Stat label="Nett Profit" value={fmtIDR(groups.totals?.profit)} tone={profitTone(groups.totals?.profit)} />
              <Stat label="Margin %" value={fmtPct(groups.totals?.margin)} tone={profitTone(groups.totals?.profit)} />
            </div>
            <Panel>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Grup</TableHead><TableHead className="text-right">Order</TableHead><TableHead className="text-right">Item</TableHead>
                    <TableHead className="text-right">Sub Total</TableHead><TableHead className="text-right">Diskon</TableHead><TableHead className="text-right">Nett</TableHead>
                    <TableHead className="text-right">HPP</TableHead><TableHead className="text-right">Profit (±)</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(groups.rows || []).length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-10 text-wmuted">Tidak ada data non-marketplace di rentang ini.</TableCell></TableRow>
                  ) : groups.rows.map((r, i) => (
                    <React.Fragment key={i}>
                      <TableRow data-testid={`recap-group-${i}`} className="cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [`grp-${i}`]: !e[`grp-${i}`] }))}>
                        <TableCell>{(r.products || []).length ? (expanded[`grp-${i}`] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}</TableCell>
                        <TableCell className="text-sm font-medium text-cocoa">{r.group}</TableCell>
                        <TableCell className="text-right text-sm">{r.orders}</TableCell>
                        <TableCell className="text-right text-sm">{r.total_items}</TableCell>
                        <TableCell className="text-right text-sm text-wmuted">{fmtIDR(r.sub_total)}</TableCell>
                        <TableCell className="text-right text-sm text-amber-600">{r.discount ? fmtIDR(r.discount) : "-"}</TableCell>
                        <TableCell className="text-right text-sm font-semibold text-status-passed">{fmtIDR(r.nett)}</TableCell>
                        <TableCell className="text-right text-sm text-wmuted">{r.hpp ? fmtIDR(r.hpp) : "-"}</TableCell>
                        <TableCell className={`text-right text-sm font-semibold ${profitTone(r.profit)}`}>{fmtIDR(r.profit)}</TableCell>
                        <TableCell className={`text-right text-sm font-medium ${profitTone(r.profit)}`}>{fmtPct(r.margin)}</TableCell>
                      </TableRow>
                      {expanded[`grp-${i}`] && (r.products || []).map((p, pi) => (
                        <TableRow key={`grp-${i}-${pi}`} className="bg-ivory/40">
                          <TableCell></TableCell>
                          <TableCell colSpan={2} className="text-xs text-wmuted pl-6">{p.sku ? <span className="font-mono">{p.sku}</span> : null} {p.name}</TableCell>
                          <TableCell className="text-right text-xs">×{p.qty}</TableCell>
                          <TableCell className="text-right text-xs text-wmuted" colSpan={6}>HPP {fmtIDR(p.hpp)}</TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
